import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { LogOut, Search, X, ArrowRight, UserCircle, ChevronDown } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import logoFullLight from '@shared/images/logo.full.light.svg';
import logoFullDark from '@shared/images/logo.full.dark.svg';
import { itemApi, Item } from '@/lib/itemApi';

export interface AppNavProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  rightSlot?: React.ReactNode;
}

export default function AppNav({ searchQuery: controlledQuery, onSearchChange, searchPlaceholder, rightSlot }: AppNavProps) {
  const { user, logout } = useAuthStore();
  const { workspaces, currentWorkspace } = useWorkspaceStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [profileOpen, setProfileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isControlled = onSearchChange !== undefined;
  const [internalQuery, setInternalQuery] = useState('');
  const query = isControlled ? (controlledQuery ?? '') : internalQuery;
  const setQuery = isControlled ? onSearchChange! : setInternalQuery;

  const [globalResults, setGlobalResults] = useState<{ workspaces: typeof workspaces; items: Item[] }>({ workspaces: [], items: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const isDashboard = location.pathname === '/dashboard';
  const isRoadmap = location.pathname.startsWith('/roadmap/');

  const placeholder = searchPlaceholder
    || (isDashboard ? 'Search workspaces...' : isRoadmap ? 'Search items...' : 'Search...');

  const doGlobalSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setGlobalResults({ workspaces: [], items: [] });
      return;
    }
    const lower = q.toLowerCase();
    const matchedWorkspaces = workspaces.filter(w => w.name.toLowerCase().includes(lower));

    let matchedItems: Item[] = [];
    const wsId = currentWorkspace?.id;
    if (wsId) {
      try {
        setSearching(true);
        const paginated = await itemApi.getPaginated(wsId, 1, 8, { search: q });
        matchedItems = paginated.data;
      } catch {
        // silent
      } finally {
        setSearching(false);
      }
    }
    setGlobalResults({ workspaces: matchedWorkspaces, items: matchedItems });
  }, [workspaces, currentWorkspace?.id]);

  useEffect(() => {
    if (isControlled) return;
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setGlobalResults({ workspaces: [], items: [] });
      setShowDropdown(false);
      return;
    }
    setShowDropdown(true);
    debounceRef.current = setTimeout(() => doGlobalSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, isControlled, doGlobalSearch]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (!isControlled && value.trim()) setShowDropdown(true);
  };

  const clearSearch = () => {
    setQuery('');
    setShowDropdown(false);
    setGlobalResults({ workspaces: [], items: [] });
  };

  const hasGlobalResults = globalResults.workspaces.length > 0 || globalResults.items.length > 0;

  return (
    <>
    <nav className="sticky top-0 z-40 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center gap-4">
          <div className="flex items-center shrink-0">
            <img src={logoFullLight} alt="Roadie" className="h-6 dark:hidden" />
            <img src={logoFullDark} alt="Roadie" className="h-6 hidden dark:block" />
          </div>

          {/* Center search */}
          <div className="flex-1 flex justify-center px-2" ref={dropdownRef}>
            <div className="relative w-full max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => { if (!isControlled && query.trim()) setShowDropdown(true); }}
                placeholder={placeholder}
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              {query && (
                <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                </button>
              )}

              {/* Global search dropdown (only for non-controlled pages) */}
              {!isControlled && showDropdown && query.trim() && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                  {searching && (
                    <div className="px-3 py-2 text-xs text-gray-400">Searching...</div>
                  )}
                  {!searching && !hasGlobalResults && (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">No results found</div>
                  )}
                  {globalResults.workspaces.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-900/50">Workspaces</div>
                      {globalResults.workspaces.map(ws => (
                        <button
                          key={ws.id}
                          onClick={() => { navigate(`/roadmap/${ws.id}`); clearSearch(); setShowDropdown(false); }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <span className="truncate">{ws.name}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                  {globalResults.items.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                        <span>Items{currentWorkspace ? ` in ${currentWorkspace.name}` : ''}</span>
                        {currentWorkspace && (
                          <button
                            onClick={() => { navigate(`/roadmap/${currentWorkspace.id}`); setShowDropdown(false); }}
                            className="text-brand-500 hover:text-brand-600 text-[10px] font-medium"
                          >
                            View roadmap
                          </button>
                        )}
                      </div>
                      {globalResults.items.map(item => (
                        <button
                          key={item.id}
                          onClick={() => { navigate(`/roadmap/${item.workspace_id}/item/${item.id}`); clearSearch(); setShowDropdown(false); }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: item.priority === 'critical' ? '#EF4444' : item.priority === 'high' ? '#F59E0B' : item.priority === 'medium' ? '#3B82F6' : '#6B7280' }} />
                            <span className="truncate">{item.title}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 shrink-0 ml-2 capitalize">{item.board_column}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <NotificationBell />
            {rightSlot}
            <ThemeToggle />
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="h-6 w-6 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-xs font-medium text-brand-700 dark:text-brand-300">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <span className="hidden sm:inline max-w-[120px] truncate">{user?.name}</span>
                <ChevronDown className="h-3 w-3 text-gray-400 hidden sm:block" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-50 py-1">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <button
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setUserMenuOpen(false); requestAnimationFrame(() => setProfileOpen(true)); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <UserCircle className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}
